import { forwardRef } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import { Link } from "react-router-dom";

const Footer = forwardRef<HTMLElement>(function Footer(_props, ref) {
  const { t, p } = useLanguage();

  return (
    <footer ref={ref} className="footer-gallery relative footer-brighten overflow-hidden">
      {/* Atmospheric warm horizon at the top of the footer (architectural light) */}
      <div
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-64 w-[120vw]"
        style={{
          background: `radial-gradient(ellipse 50% 60% at 50% 0%, hsl(var(--warm) / 0.06) 0%, transparent 70%)`,
        }}
        aria-hidden="true"
      />

      <div className="container relative z-10 mx-auto px-4 py-20 lg:px-8">
        {/* Top row */}
        <div className="mb-14 flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <div>
            <h2 className="brand-logotype text-[13px] text-foreground">
              Frank Pistorius
            </h2>

            <p className="mt-1.5 text-xs text-muted-foreground">
              {t.footer.tagline}
            </p>
          </div>
          <div className="max-w-xs md:text-right">
            <p className="text-[13px] font-medium text-foreground">
              {t.footer.stayConnected}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t.footer.stayConnectedText}
            </p>
          </div>
        </div>

        {/* Columns */}
        <div className="grid gap-10 pt-12 sm:grid-cols-3">
          <nav aria-label={t.footer.forCustomers}>
            <h3 className="footer-heading">{t.footer.forCustomers}</h3>
            <div className="footer-cluster">
              <Link to={p("products")} className="footer-link">{t.nav.products}</Link>
              <Link to={p("about")} className="footer-link">{t.nav.about}</Link>
              <Link to={p("news")} className="footer-link">Journal</Link>
              <Link to={p("contact")} className="footer-link">{t.nav.contact}</Link>
            </div>
          </nav>

          <nav aria-label={t.footer.support}>
            <h3 className="footer-heading">{t.footer.support}</h3>
            <div className="footer-cluster">
              <Link to={p("contact")} className="footer-link">{t.contact.contactUs}</Link>
              <a href="mailto:info@frankpistorius.de" className="footer-link">info@frankpistorius.de</a>
              <a href="tel:+491773078721" className="footer-link">+49 177 307 8721</a>
            </div>
          </nav>

          <nav aria-label={t.footer.followUs}>
            <h3 className="footer-heading">{t.footer.followUs}</h3>
            <div className="footer-cluster">
              {/* Social profiles intentionally omitted until verified business URLs are available. */}
              <a href="mailto:info@frankpistorius.de" className="footer-link">info@frankpistorius.de</a>
            </div>
          </nav>
        </div>

        {/* Bottom */}
        <div className="mt-14 flex flex-col gap-3 pt-8 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} Frank Pistorius. {t.footer.rights}</p>
          <nav aria-label="Legal" className="flex flex-wrap items-center gap-2">
            {([
              { key: "impressum" as const, label: t.footer.impressum },
              { key: "datenschutz" as const, label: t.footer.datenschutz },
              { key: "cookiePolicy" as const, label: t.footer.cookiePolicy },
              { key: "terms" as const, label: t.footer.terms },
            ]).map(({ key, label }) => (
              <Link
                key={key}
                to={p(key)}
                className="chip-base chip-outline"
              >
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
});

export default Footer;
